<template lang="pug">
div(class="my-requests-container")

    //- Header
    div(class="header-bar")
        div(class="header-icon-wrap")
            b-icon(icon="journal-check" font-scale="1.8")
        div
            h6(class="header-title") My Requests
            small(class="text-muted") All your submitted requests

    div(class="requests-body")

        //- Filter pills
        div(class="filter-bar")
            button(class="filter-pill" :class="filter === 'all' ? 'filter-pill--active' : ''" @click="filter = 'all'")
                | All
                span(class="filter-count") {{ baseRequests.length }}
            button(class="filter-pill filter-pill--warning" :class="filter === 'pending' ? 'filter-pill--active filter-pill--warning-active' : ''" @click="filter = 'pending'")
                | Pending
                span(class="filter-count") {{ pendingRequests.length }}
            button(class="filter-pill filter-pill--success" :class="filter === 'processed' ? 'filter-pill--success-active' : ''" @click="filter = 'processed'")
                | Approved
                span(class="filter-count") {{ approveRequest.length }}
            button(class="filter-pill filter-pill--danger" :class="filter === 'rejected' ? 'filter-pill--danger-active' : ''" @click="filter = 'rejected'")
                | Rejected
                span(class="filter-count") {{ rejectedRequests.length }}

        //- Loading
        div(v-if="loading" class="empty-state")
            b-spinner(variant="primary" style="width:2.5rem;height:2.5rem")
            p(class="mt-3 text-muted") Loading your requests…

        //- Empty
        div(v-else-if="!filteredRequests.length" class="empty-state")
            div(class="empty-icon-wrap")
                b-icon(icon="inbox" font-scale="2.5")
            p(class="mt-3 mb-1 fw-600") No {{ filter !== 'all' ? filter : '' }} requests found
            b-button(variant="outline-primary" size="sm" @click="loadRequests" class="mt-2 refresh-btn")
                b-icon(icon="arrow-clockwise" class="mr-1")
                | Refresh

        //- Request cards
        div(v-else class="requests-list")
            div(
                v-for="request in filteredRequests"
                :key="request.crb5c_fowsessionmakeupid"
                class="request-card"
                :class="'request-card--' + getTypeKey(request.crb5c_requesttype)"
            )
                //- Card header row
                div(class="card-head")
                    div(class="card-head-left")
                        span(class="type-chip" :class="'type-chip--' + getTypeKey(request.crb5c_requesttype)")
                            b-icon(:icon="getTypeIcon(request.crb5c_requesttype)" class="mr-1")
                            | {{ getTypeLabel(request.crb5c_requesttype) }}
                        div(class="card-date mt-1") {{ formatDate(request.crb5c_originaldate) }}
                    span(class="status-pill" :class="'status-pill--' + getStatusKey(request.crb5c_requeststatus)")
                        | {{ getStatusLabel(request.crb5c_requeststatus) }}


                div(v-if="request.crb5c_requesttype === 0" class="swap-block")
                    div(class="swap-col")
                        div(class="swap-label") From
                        div(class="swap-date") {{ formatDate(request.crb5c_originaldate) }}
                        div(class="swap-sched") {{ request['_crb5c_originalscheduleid_value@OData.Community.Display.V1.FormattedValue'] }}
                    b-icon(icon="arrow-right" font-scale="1.4" class="swap-arrow mx-2")
                    div(class="swap-col swap-col--right")
                        div(class="swap-label") To
                        div(class="swap-date") {{ formatDate(request.crb5c_replacementdate) }}
                        div(class="swap-sched") {{ request['_crb5c_replacementscheduleid_value@OData.Community.Display.V1.FormattedValue'] }}


                div(v-else class="mc-block")
                    div(v-if="request.crb5c_mcdaterange" class="mc-range-row mb-2")
                        b-icon(icon="calendar-range" class="mr-1" variant="primary")
                        strong(class="mr-1") MC Leave:
                        span {{ formatDateRange(request.crb5c_mcdaterange) }}
                    span(class="detail-label") Reason
                    p(class="detail-text") {{ request.crb5c_reason || 'No reason provided' }}
                    div(v-if="hasDocument(request)" class="doc-list")
                        b-button(
                            v-for="doc in getDocumentNames(request)"
                            :key="doc.index"
                            size="sm"
                            variant="outline-primary"
                            class="doc-btn mr-1 mb-1"
                            @click="downloadDocument(request, doc.index)"
                            :disabled="isDownloadingDoc(request.crb5c_fowsessionmakeupid, doc.index)"
                        )
                            b-spinner(small v-if="isDownloadingDoc(request.crb5c_fowsessionmakeupid, doc.index)")
                            b-icon(icon="file-earmark-arrow-down" v-else class="mr-1")
                            | Supporting Doc
                    div(v-else class="no-doc")
                        b-icon(icon="paperclip" class="mr-1")
                        | No document attached

                div(v-if="request.crb5c_requeststatus === 2 && request.crb5c_rejectionreason" class="rejection-block")
                    b-icon(icon="exclamation-triangle-fill" class="mr-1")
                    strong Rejection: &nbsp;
                    span {{ request.crb5c_rejectionreason }}

                //- Approved MC/Other: set the credit-note refund expectation.
                //- Kept conditional-tense — an approved MC date may be refunded
                //- via credit note (if already invoiced) or simply left off the
                //- next invoice (no credit note involved).
                div(v-if="request.crb5c_requesttype !== 0 && request.crb5c_requeststatus === 1" class="cn-timing-block")
                    b-icon(icon="info-circle-fill" class="mr-1")
                    span
                        | Dates already billed will be refunded by credit note — processing takes approximately
                        strong  6–8 weeks
                        | . Dates not yet billed are simply excluded from your next invoice.
                    

                div(class="card-foot")
                    span.my-2(v-if="request.crb5c_rejectionreason")
                            strong Resubmit again in the "Submit MC" page
                    span
                        b-icon(icon="calendar3" class="mr-1")
                        | Submitted: {{ formatDateTime(request.createdon) }}
                    span(v-if="request.modifiedon && request.crb5c_isprocessed")
                        b-icon(icon="check-circle" class="mr-1")
                        | Processed: {{ formatDateTime(request.modifiedon) }}
                    

        //- Refresh at bottom
        div(v-if="!loading && filteredRequests.length" class="text-center mt-3 pb-2")
            b-button(variant="outline-primary" size="sm" @click="loadRequests" class="refresh-btn")
                b-icon(icon="arrow-clockwise" class="mr-1")
                | Refresh List
</template>

<script>
import dayjs from 'dayjs';

export default {
    props: {
        userId: {
            type: String,
            required: true
        },
        // 'swap' shows only session swaps, 'mc' only MC/Other requests — the
        // two features now live on separate pages. null shows everything.
        typeFilter: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            loading: false,
            filter: 'pending',
            allRequests: [],
            downloadingDoc: null
        };
    },
    computed: {
        baseRequests() {
            if (this.typeFilter === 'swap') {
                return this.allRequests.filter(r => r.crb5c_requesttype === 0);
            }
            if (this.typeFilter === 'mc') {
                return this.allRequests.filter(r => r.crb5c_requesttype !== 0);
            }
            return this.allRequests;
        },
        pendingRequests() {
            return this.baseRequests.filter(r => r.crb5c_requeststatus === 0);
        },
        approveRequest() {
            return this.baseRequests.filter(r => r.crb5c_requeststatus === 4 || r.crb5c_requeststatus === 1);
        },
        rejectedRequests() {
            return this.baseRequests.filter(r => r.crb5c_requeststatus === 2);
        },
        filteredRequests() {
            if (this.filter === 'pending') return this.pendingRequests;
            if (this.filter === 'processed') return this.approveRequest;
            if (this.filter === 'rejected') return this.rejectedRequests;
            return this.baseRequests;
        }
    },
    mounted() {
        this.loadRequests();
    },
    methods: {
        async loadRequests() {
            this.loading = true;
            try {
                const { data } = await this.$root.axios.get('/makeup_sessions/my-requests', {
                    params: {
                        userID: this.userId
                    }
                });
                
                if (data.success) {
                    // Sort by creation date (newest first)
                    this.allRequests = data.requests.sort((a, b) => 
                        dayjs(b.createdon).unix() - dayjs(a.createdon).unix()
                    );
                }
            } catch (error) {
                console.error('Error loading requests:', error);
                this.$bvToast.toast('Failed to load requests. Please try again.', {
                    title: 'Error',
                    variant: 'danger',
                    solid: true
                });
            } finally {
                this.loading = false;
            }
        },
        hasDocument(request) {
            if (!request.crb5c_file) return false;
            try {
                const fileData = JSON.parse(request.crb5c_file);
                if (Array.isArray(fileData)) return fileData.length > 0;
                return !!fileData.sharepointId;
            } catch {
                return false;
            }
        },
        getDocumentNames(request) {
            if (!request.crb5c_file) return [];
            try {
                const fileData = JSON.parse(request.crb5c_file);
                const list = Array.isArray(fileData) ? fileData : [fileData];
                return list
                    .filter(f => f.sharepointId)
                    .map((f, i) => ({ index: i, filename: f.filename || `Document ${i + 1}` }));
            } catch {
                return [];
            }
        },
        isDownloadingDoc(requestId, docIndex) {
            return this.downloadingDoc === `${requestId}-${docIndex}`;
        },
        async downloadDocument(request, docIndex = 0) {
            const key = `${request.crb5c_fowsessionmakeupid}-${docIndex}`;
            this.downloadingDoc = key;
            
            try {
                // Get fresh download URL(s) from endpoint
                const { data } = await this.$root.axios.get(
                    `/makeup_sessions/${request.crb5c_fowsessionmakeupid}/document`
                );
                
                const docUrl = data.documents?.[docIndex]?.downloadUrl ||
                    (docIndex === 0 ? data.downloadUrl : null);
                
                if (data.success && docUrl) {
                    // Open in new tab
                    window.open(docUrl, '_blank');
                    
                    this.$bvToast.toast('Document opened in new tab', {
                        title: 'Success',
                        variant: 'success',
                        solid: true,
                        autoHideDelay: 2000
                    });
                } else {
                    throw new Error('Document URL not available');
                }
            } catch (error) {
                console.error('Error downloading document:', error);
                this.$bvToast.toast(error.response?.data?.error || 'Failed to download document. Please try again.', {
                    title: 'Error',
                    variant: 'danger',
                    solid: true
                });
            } finally {
                this.downloadingDoc = null;
            }
        },
        getTypeLabel(type) {
            const types = {
                0: 'Make Up',
                1: 'MC',
                2: 'Other'
            };
            return types[type] || 'Unknown';
        },
        getTypeVariant(type) {
            const variants = {
                0: 'primary',
                1: 'warning',
                2: 'info'
            };
            return variants[type] || 'secondary';
        },
        getStatusLabel(status) {
            const labels = {
                0: 'Pending',
                1: 'Approved',
                2: 'Rejected',
                4: 'Submitted'
            };
            return labels[status] || 'Unknown';
        },
        getStatusVariant(status) {
            const variants = {
                0: 'warning',
                1: 'success',
                2: 'danger',
                4: 'info'
            };
            return variants[status] || 'secondary';
        },
        formatDateRange(rangeJson) {
            try {
                const obj = JSON.parse(rangeJson);
                // Selective days: list them. Range / legacy {start,end}: show the span.
                if (obj.mode === 'selective' || (!obj.start && Array.isArray(obj.dates))) {
                    const ds = (obj.dates || []).map(d => dayjs(d).format('D MMM'));
                    return ds.length ? `${ds.length} day${ds.length > 1 ? 's' : ''}: ${ds.join(', ')}` : '—';
                }
                const start = dayjs(obj.start).format('D MMM YYYY');
                const end = dayjs(obj.end).format('D MMM YYYY');
                return `${start} – ${end}`;
            } catch {
                return rangeJson;
            }
        },
        formatDate(dateStr) {
            if (!dateStr) return 'N/A';
            return dayjs(dateStr).format('ddd, MMM D, YYYY');
        },
        formatDateTime(dateStr) {
            if (!dateStr) return 'N/A';
            return dayjs(dateStr).format('MMM D, YYYY h:mm A');
        },
        getTypeKey(type) {
            return { 0: 'swap', 1: 'mc', 2: 'other' }[type] || 'other';
        },
        getTypeIcon(type) {
            return { 0: 'arrow-repeat', 1: 'file-medical', 2: 'file-earmark-text' }[type] || 'file-earmark';
        },
        getStatusKey(status) {
            return { 0: 'pending', 1: 'approved', 2: 'rejected', 4: 'submitted' }[status] || 'unknown';
        },
        // True once any of this MC's dates have been sent to finance for a
        // credit note (the billing app's CN export writes the ledger).
        hasCnApplied(request) {
            try {
                const arr = JSON.parse(request.crb5c_mc_applied_dates || '[]');
                return Array.isArray(arr) && arr.length > 0;
            } catch (e) {
                return false;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
$purple: #8967B3;
$radius-lg: 16px;
$radius-md: 12px;
$radius-sm: 8px;
$shadow-sm: 0 2px 8px rgba(0,0,0,0.08);
$transition: all 0.22s ease;

.my-requests-container {
    min-height: 55vh;
    background: #f7f6fb;
    border-radius: $radius-lg;
    overflow: hidden;
}

// Header
.header-bar {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem 1.25rem;
    background: white;
    border-bottom: 1px solid #ede9f4;
}
.header-icon-wrap {
    width: 44px;
    height: 44px;
    background: #ede9f4;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $purple;
    flex-shrink: 0;
}
.header-title { font-weight: 700; margin: 0; color: #222; }

// Body
.requests-body { padding: 1rem; }

// Filter bar
.filter-bar {
    display: flex;
    gap: 0.4rem;
    flex-wrap: wrap;
    margin-bottom: 1rem;
}
.filter-pill {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 0.3rem 0.8rem;
    border-radius: 20px;
    border: 1.5px solid #d0c8e8;
    background: white;
    color: #888;
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;
    transition: $transition;
    &:focus { outline: none; }
    &--active         { background: $purple;   border-color: $purple;   color: white; }
    &--warning-active { background: #e0a800;   border-color: #e0a800;   color: white; }
    &--success-active { background: #28a745;   border-color: #28a745;   color: white; }
    &--danger-active  { background: #dc3545;   border-color: #dc3545;   color: white; }
    &:hover:not(.filter-pill--active):not(.filter-pill--warning-active):not(.filter-pill--success-active):not(.filter-pill--danger-active) {
        border-color: $purple;
        color: $purple;
    }
}
.filter-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: rgba(0,0,0,0.1);
    font-size: 0.65rem;
    font-weight: 700;
}

// Empty state
.empty-state { text-align: center; padding: 3rem 1rem; }
.empty-icon-wrap {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    background: #ede9f4;
    border-radius: 50%;
    color: $purple;
}

// Requests list
.requests-list { display: flex; flex-direction: column; gap: 0.75rem; }

.request-card {
    background: white;
    border-radius: $radius-md;
    border-left: 4px solid $purple;
    box-shadow: $shadow-sm;
    padding: 0.9rem 1rem;
    transition: $transition;
    &:hover { transform: translateY(-2px); box-shadow: 0 4px 16px rgba(0,0,0,0.12); }
    &--swap  { border-left-color: #1a73e8; }
    &--mc    { border-left-color: #e65100; }
    &--other { border-left-color: #00695c; }
}

// Card head
.card-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 0.75rem;
}
.card-head-left { flex: 1; }
.type-chip {
    display: inline-flex;
    align-items: center;
    font-size: 0.72rem;
    font-weight: 700;
    padding: 0.15rem 0.65rem;
    border-radius: 20px;
    letter-spacing: 0.3px;
    &--swap  { background: #e8f0fe; color: #1a73e8; }
    &--mc    { background: #fff3e0; color: #e65100; }
    &--other { background: #e8f5e9; color: #00695c; }
}
.card-date { font-size: 0.82rem; font-weight: 600; color: #333; }
.status-pill {
    display: inline-flex;
    align-items: center;
    font-size: 0.7rem;
    font-weight: 700;
    padding: 0.2rem 0.7rem;
    border-radius: 20px;
    white-space: nowrap;
    flex-shrink: 0;
    &--pending   { background: #fff3cd; color: #856404; }
    &--approved  { background: #d4edda; color: #155724; }
    &--rejected  { background: #f8d7da; color: #721c24; }
    &--submitted { background: #d1ecf1; color: #0c5460; }
    &--unknown   { background: #e2e3e5; color: #383d41; }
}

// Swap block
.swap-block {
    display: flex;
    align-items: center;
    background: #f7f6fb;
    border-radius: $radius-sm;
    padding: 0.65rem 0.75rem;
    margin-bottom: 0.6rem;
}
.swap-col { flex: 1; &--right { text-align: right; } }
.swap-label { font-size: 0.62rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: #aaa; }
.swap-date  { font-size: 0.82rem; font-weight: 700; color: #222; margin-top: 1px; }
.swap-sched { font-size: 0.68rem; color: #888; margin-top: 1px; }
.swap-arrow { color: $purple; flex-shrink: 0; }

// MC/Other block
.mc-block {
    background: #f7f6fb;
    border-radius: $radius-sm;
    padding: 0.65rem 0.75rem;
    margin-bottom: 0.6rem;
}
.detail-label { font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: #aaa; display: block; }
.detail-text  { font-size: 0.82rem; color: #333; margin: 0.25rem 0 0.6rem; }
.doc-list { display: flex; flex-wrap: wrap; gap: 0.25rem; }
.doc-btn  { border-radius: 20px; font-size: 0.75rem; }
.no-doc   { font-size: 0.75rem; color: #aaa; }

// Rejection block
.rejection-block {
    background: #fff5f5;
    border-radius: $radius-sm;
    padding: 0.5rem 0.75rem;
    font-size: 0.8rem;
    color: #c0392b;
    margin-bottom: 0.5rem;
    border-left: 3px solid #dc3545;
}

.cn-timing-block {
    background: #f4effa;
    border-radius: $radius-sm;
    padding: 0.5rem 0.75rem;
    font-size: 0.8rem;
    color: #50276b;
    margin-bottom: 0.5rem;
    border-left: 3px solid $purple;
}

// Card footer
.card-foot {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.25rem;
    font-size: 0.68rem;
    color: #aaa;
    margin-top: 0.5rem;
    padding-top: 0.5rem;
    border-top: 1px solid #ede9f4;
}

// Utilities
.fw-600 { font-weight: 600; }
.refresh-btn { border-radius: 20px; font-size: 0.8rem; }
</style>
